/**
 * HumanAIProject - Research wing landing page
 *
 * Features:
 * - Mission statement
 * - Funding transparency (supporter count, how funds are used)
 * - Team section
 * - Newsletter signup
 * - Donation CTA
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Heart, Users, BookOpen, Sparkles, Target, ArrowRight } from 'lucide-react'
import { FadeIn, StaggerContainer } from '@/components/motion'
import { NewsletterSignup } from '@/components/NewsletterSignup'
import { DonationWidget } from '@/components/DonationWidget'
import { motion } from 'framer-motion'
import { publicFetch } from '@/services/api'
import { setPageMeta } from '@/utils/seo'

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] as const } },
}

interface Stats {
  subscribers: number
  supporters: number
}

export default function HumanAIProject() {
  const [stats, setStats] = useState<Stats>({ subscribers: 0, supporters: 0 })

  useEffect(() => {
    setPageMeta({
      title: 'The Human+AI Project | Research for Human-AI Coexistence',
      description: 'The Human+AI Project: Building tools and publishing insights that help humans thrive alongside artificial intelligence. Support independent AI research.',
      canonical: 'https://returned.com/human-ai-project',
    })

    // Fetch stats
    Promise.all([
      publicFetch('/api/newsletter/count').then(r => r.json()).catch(() => ({ total: 0 })),
      publicFetch('/api/donate/stats').then(r => r.json()).catch(() => ({ supporters_this_month: 0 })),
    ]).then(([newsletter, donations]) => {
      setStats({
        subscribers: newsletter.total || 0,
        supporters: donations.supporters_this_month || 0,
      })
    })
  }, [])

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff' }}>
      {/* Hero Section */}
      <section className="landing-section" style={{ paddingBottom: 'clamp(2rem, 4vw, 4rem)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <FadeIn>
            {/* Back Link */}
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6366f1',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                marginBottom: '2rem',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#818cf8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6366f1'}
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>

            {/* Logo/Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              background: 'rgba(99, 102, 241, 0.10)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Sparkles size={40} color="#a5b4fc" />
            </div>

            <h1 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: 700,
              color: '#0f0f0f',
              marginBottom: '1rem',
              letterSpacing: '-0.02em',
            }}>
              <span style={{
                color: '#0f0f0f',
              }}>The Human+AI Project</span>
            </h1>

            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
              color: '#5f5f5f',
              maxWidth: '600px',
              margin: '0 auto 2rem',
              lineHeight: 1.7,
            }}>
              AI solutions for humans, so that AI and humans can co-exist. We build tools and publish insights that help people thrive in an AI-powered world.
            </p>

            {/* Stats */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#6366f1',
                }}>
                  {stats.subscribers || '—'}
                </div>
                <div style={{ color: '#5f5f5f', fontSize: '0.9rem' }}>Subscribers</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#ec4899',
                }}>
                  {stats.supporters || '—'}
                </div>
                <div style={{ color: '#5f5f5f', fontSize: '0.9rem' }}>Supporters this month</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Mission Section */}
      <section style={{ padding: '0 1rem 4rem' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <StaggerContainer stagger={0.1}>
            <motion.div variants={staggerItem} style={{
              background: '#fafafa',
              border: '1px solid #eaeaea',
              borderRadius: '20px',
              padding: 'clamp(1.5rem, 4vw, 2.5rem)',
              marginBottom: '2rem',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}>
                <Target size={24} color="#6366f1" />
                <h2 style={{ color: '#0f0f0f', fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>Our Mission</h2>
              </div>
              <p style={{
                color: '#3f3f3f',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                margin: 0,
              }}>
                As AI becomes more capable, we believe humans need practical guidance—not fear-mongering or hype.
                The Human+AI Project exists to bridge the gap between cutting-edge AI development and everyday people
                trying to understand what it means for their lives, their work, and their futures.
              </p>
            </motion.div>

            {/* What We Do */}
            <motion.div variants={staggerItem} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem',
            }}>
              {[
                {
                  icon: <BookOpen size={24} />,
                  title: 'Weekly Insights',
                  description: 'Thoughtful analysis on AI trends, emerging tech, and what it means for society.',
                },
                {
                  icon: <Sparkles size={24} />,
                  title: 'Tools & Resources',
                  description: 'Practical guides and tools to help you work effectively alongside AI.',
                },
                {
                  icon: <Users size={24} />,
                  title: 'Community',
                  description: 'Connect with others exploring human-AI coexistence and share learnings.',
                },
              ].map((item, i) => (
                <div key={i} style={{
                  background: '#fafafa',
                  border: '1px solid #eaeaea',
                  borderRadius: '16px',
                  padding: '1.5rem',
                }}>
                  <div style={{ color: '#6366f1', marginBottom: '1rem' }}>{item.icon}</div>
                  <h3 style={{ color: '#0f0f0f', fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {item.title}
                  </h3>
                  <p style={{ color: '#5f5f5f', fontSize: '0.95rem', margin: 0, lineHeight: 1.6 }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* How Funds Are Used */}
            <motion.div variants={staggerItem} style={{
              background: 'rgba(99, 102, 241, 0.10)',
              border: '1px solid rgba(236, 72, 153, 0.2)',
              borderRadius: '20px',
              padding: 'clamp(1.5rem, 4vw, 2.5rem)',
              marginBottom: '2rem',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
              }}>
                <Heart size={24} color="#ec4899" />
                <h2 style={{ color: '#0f0f0f', fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>How Your Support Helps</h2>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
              }}>
                {[
                  { label: 'Research & Writing', percent: 50, desc: 'Deep-dive articles, analysis' },
                  { label: 'Tools & Resources', percent: 30, desc: 'Free tools for the community' },
                  { label: 'Operations', percent: 20, desc: 'Hosting, infrastructure' },
                ].map((item, i) => (
                  <div key={i}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                    }}>
                      <span style={{ color: '#0f0f0f', fontWeight: 500 }}>{item.label}</span>
                      <span style={{ color: '#ec4899', fontWeight: 600 }}>{item.percent}%</span>
                    </div>
                    <div style={{
                      height: '6px',
                      background: '#f5f5f5',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '0.5rem',
                    }}>
                      <div style={{
                        width: `${item.percent}%`,
                        height: '100%',
                        background: 'rgba(99, 102, 241, 0.10)',
                        borderRadius: '3px',
                      }} />
                    </div>
                    <div style={{ color: '#5f5f5f', fontSize: '0.8rem' }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Newsletter Signup */}
            <motion.div variants={staggerItem}>
              <NewsletterSignup variant="hero" source="research-page" />
            </motion.div>

            {/* Donation Widget */}
            <motion.div variants={staggerItem}>
              <DonationWidget />
            </motion.div>

            {/* Read the Blog CTA */}
            <motion.div variants={staggerItem} style={{
              textAlign: 'center',
              marginTop: '2rem',
            }}>
              <Link
                to="/blog"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#6366f1',
                  textDecoration: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  padding: '1rem 2rem',
                  background: 'rgba(99, 102, 241, 0.1)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '12px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                Read Our Latest Articles
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </StaggerContainer>
        </div>
      </section>

    </div>
  )
}
